/**
 * The groups list's membership state from the user's OWN rows, in memory.
 *
 * The list used to run three queries per listed group (virtual membership,
 * editor, organization member) — about a hundred round trips before anything
 * showed. The same answers come from two queries (the user's groupMembers and
 * organizationMembers rows) plus the groups already loaded. Pure, no Firebase.
 *
 * Same rules as before:
 *  - a group: a member when the user has a groupMembers row for it;
 *  - a section (groupType 'parent'): a member when the user is a member of one
 *    of its groups (same organization) — virtual-membership isVirtualMember;
 *  - editor: the group's creator, or a groupMembers row with role 'editor';
 *  - org member: any organizationMembers row for the group's organization.
 */

export interface ListGroup {
  id: string;
  groupType?: string;
  parentGroupId?: string;
  organization?: string | null;
  creatorId?: string;
}

export interface MembershipRow {
  groupId?: string;
  role?: string;
}

export interface ListMembership {
  memberIds: Set<string>;
  editorIds: Set<string>;
  orgMemberGroupIds: Set<string>;
}

export function resolveListMembership(
  groups: ReadonlyArray<ListGroup>,
  userId: string,
  memberRows: ReadonlyArray<MembershipRow>,
  userOrgIds: Iterable<string>,
): ListMembership {
  const direct = new Set<string>();
  const editorRows = new Set<string>();
  for (const row of memberRows) {
    if (!row.groupId) continue;
    direct.add(row.groupId);
    if (row.role === 'editor') editorRows.add(row.groupId);
  }
  const orgs = new Set(userOrgIds);

  const memberIds = new Set<string>();
  const editorIds = new Set<string>();
  const orgMemberGroupIds = new Set<string>();
  for (const g of groups) {
    if (g.groupType === 'parent') {
      const org = g.organization || null;
      const inChild = groups.some(
        (c) => c.parentGroupId === g.id && (c.organization || null) === org && direct.has(c.id),
      );
      if (inChild) memberIds.add(g.id);
    } else if (direct.has(g.id)) {
      memberIds.add(g.id);
    }
    if ((userId && g.creatorId === userId) || editorRows.has(g.id)) editorIds.add(g.id);
    if (g.organization && orgs.has(g.organization)) orgMemberGroupIds.add(g.id);
  }
  return { memberIds, editorIds, orgMemberGroupIds };
}

export interface CountRow {
  groupId?: string;
  userId?: string;
}

/**
 * Members per listed group from groupMembers rows: distinct users of a group,
 * and for a section the distinct users across its groups (a person in two of
 * its groups counts once), as getGroupMembersVirtual did.
 */
export function countListMembers(
  listed: ReadonlyArray<ListGroup>,
  allGroups: ReadonlyArray<ListGroup>,
  rows: ReadonlyArray<CountRow>,
): Record<string, number> {
  const byGroup = new Map<string, Set<string>>();
  for (const r of rows) {
    if (!r.groupId || !r.userId) continue;
    if (!byGroup.has(r.groupId)) byGroup.set(r.groupId, new Set());
    byGroup.get(r.groupId)!.add(r.userId);
  }
  const counts: Record<string, number> = {};
  for (const g of listed) {
    if (g.groupType === 'parent') {
      const org = g.organization || null;
      const users = new Set<string>();
      for (const c of allGroups) {
        if (c.parentGroupId !== g.id || (c.organization || null) !== org) continue;
        byGroup.get(c.id)?.forEach((u) => users.add(u));
      }
      counts[g.id] = users.size;
    } else {
      counts[g.id] = byGroup.get(g.id)?.size ?? 0;
    }
  }
  return counts;
}

/** The group ids whose groupMembers rows the counts need (sections → their groups). */
export function countSourceIds(listed: ReadonlyArray<ListGroup>, allGroups: ReadonlyArray<ListGroup>): string[] {
  const ids = new Set<string>();
  for (const g of listed) {
    if (g.groupType === 'parent') {
      const org = g.organization || null;
      for (const c of allGroups) {
        if (c.parentGroupId === g.id && (c.organization || null) === org) ids.add(c.id);
      }
    } else {
      ids.add(g.id);
    }
  }
  return [...ids];
}
